import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, Anchor, Award, ShieldCheck, Share2, ArrowLeft, ArrowRight, Edit, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function PublicProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pro, setPro] = useState(null);
  const [services, setServices] = useState([]);
  const isOwnProfile = user?.id === id;

  useEffect(() => {
    api.get(`/professionals/${id}`).then(r => setPro(r.data)).catch(e => toast.error(apiErr(e)));
    api.get("/services").then(r => setServices(r.data));
  }, [id]);

  const svcName = (id) => services.find(s => s.id === id)?.name || id;

  if (!pro) return <div className="hub-cream min-h-screen"><Navbar/><div className="p-20 text-center">Cargando...</div></div>;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: pro.name, url }); return; }
      catch (e) { console.warn("native share cancelled", e); }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Enlace copiado. Compártelo en Instagram, WhatsApp o donde quieras.");
  };

  const prof = pro.professional || {};

  return (
    <div className="min-h-screen hub-cream">
      <Navbar/>
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-[#5C6B7D] mb-6 font-display" data-testid="pp-back">
          <ArrowLeft size={14}/> Volver
        </button>

        <div className="hub-card overflow-hidden">
          <div className="relative h-48 hub-navy">
            <img src="https://images.pexels.com/photos/38510508/pexels-photo-38510508.jpeg" className="absolute inset-0 w-full h-full object-cover opacity-40" alt=""/>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1D3A]/80 to-transparent"/>
          </div>
          <div className="p-8 md:p-10 -mt-14 relative">
            {prof.photo_path ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${prof.photo_path}`}
                alt={pro.name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"/>
            ) : (
              <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center font-serif text-4xl text-[#0B1D3A]">
                {pro.name?.[0] || "?"}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-serif text-4xl text-[#0B1D3A] tracking-tight">{pro.name}</h1>
                  {prof.kyc_status === "verified" && <span className="hub-pill bg-emerald-100 text-emerald-800" title="KYC verificado por MarinaHub"><ShieldCheck size={11}/> KYC verificado</span>}
                  {prof.video_path && <span className="hub-pill bg-[#FF6B57]/10 text-[#FF6B57]">🎬 Con vídeo</span>}
                  {prof.stripe_payouts_enabled && <span className="hub-pill bg-blue-50 text-blue-700">💳 Cobros ok</span>}
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-[#5C6B7D] flex-wrap">
                  <span className="flex items-center gap-1"><Star size={14} className="fill-[#FF6B57] text-[#FF6B57]"/> <strong className="text-[#0B1D3A]">{(prof.rating || 0).toFixed(1)}</strong> ({prof.reviews_count || 0} reseñas)</span>
                  <span className="flex items-center gap-1"><Award size={14}/> {prof.jobs_completed || 0} trabajos</span>
                  <span className="flex items-center gap-1"><MapPin size={14}/> {(prof.coverage_provinces || []).join(", ") || prof.coverage_province || "Toda España"}</span>
                  {prof.years_experience > 0 && <span className="flex items-center gap-1">🎯 {prof.years_experience} años exp.</span>}
                </div>
              </div>
              <Button onClick={share} className="hub-btn-primary gap-2 rounded-full" data-testid="pp-share">
                <Share2 size={14}/> Compartir
              </Button>
            </div>

            {prof.bio && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-2">Sobre este profesional</div>
                <p className="text-[#0B1D3A] leading-relaxed max-w-3xl">{prof.bio}</p>
                {(prof.languages && prof.languages.length > 0) && (
                  <div className="mt-3 text-sm text-[#5C6B7D]">
                    <b>Idiomas:</b> {prof.languages.join(" · ")}
                  </div>
                )}
              </div>
            )}

            {prof.video_path && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-2">Presentación en vídeo</div>
                <video src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${prof.video_path}`} controls playsInline preload="metadata"
                  className="w-full max-w-md rounded-2xl border border-[#0B1D3A]/10 shadow-sm" data-testid="pro-video"/>
              </div>
            )}

            {(prof.gallery && prof.gallery.length > 0) && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-2">Trabajos anteriores</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {prof.gallery.map((g) => (
                    <img key={g} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${g}`}
                      className="w-full h-32 object-cover rounded-lg border border-[#0B1D3A]/10" alt="Trabajo previo"/>
                  ))}
                </div>
              </div>
            )}

            {(prof.blocked_days && prof.blocked_days.length > 0) && (() => {
              const today = new Date().toISOString().slice(0, 10);
              const upcoming = prof.blocked_days.filter(d => d >= today).slice(0, 6);
              if (!upcoming.length) return null;
              return (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-2 flex items-center gap-1"><CalendarIcon size={11}/> Días no disponibles próximos</div>
                  <div className="flex flex-wrap gap-1.5">
                    {upcoming.map(d => (
                      <span key={d} className="hub-pill bg-red-50 text-red-700 border border-red-200 text-[11px]">
                        {new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#5C6B7D] italic mt-2">Al pedir cita, evita estos días.</p>
                </div>
              );
            })()}

            <div className="mt-8">
              <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-3">Servicios</div>
              <div className="flex flex-wrap gap-2">
                {(prof.services || []).map((s) => (
                  <span key={s} className="hub-pill hub-sand text-[#0B1D3A]">{svcName(s)}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-3">Últimas reseñas</div>
          <div className="grid md:grid-cols-2 gap-3">
            {(pro.recent_reviews || []).length === 0 && <div className="hub-card p-6 text-sm text-[#5C6B7D] col-span-2">Aún sin reseñas públicas.</div>}
            {(pro.recent_reviews || []).map((r) => (
              <div key={r.id} className="hub-card p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(n => <Star key={n} size={14} className={n <= r.rating ? "fill-[#FF6B57] text-[#FF6B57]" : "text-[#0B1D3A]/15"}/>)}
                </div>
                {r.comment && <p className="mt-3 text-sm text-[#0B1D3A]">"{r.comment}"</p>}
                <div className="mt-3 text-xs text-[#5C6B7D] font-display">— {r.owner_name}</div>
              </div>
            ))}
          </div>
        </div>

        {isOwnProfile ? (
          <div className="mt-10 hub-card p-8">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#FF6B57]/10 text-[#FF6B57] flex items-center justify-center shrink-0"><Edit size={18}/></div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl text-[#0B1D3A]">Este es tu perfil público</h3>
                <p className="text-[#5C6B7D] text-sm mt-1">Así te ven los propietarios cuando reciben tus ofertas. Actualiza servicios, zona de trabajo o vídeo desde tu panel.</p>
                <Button onClick={() => nav("/pro")} className="hub-btn-primary mt-4 gap-2 rounded-full" data-testid="pp-edit-cta">
                  <Edit size={14}/> Editar mi perfil
                </Button>
              </div>
            </div>
          </div>
        ) : user?.role !== "professional" && (
          <div className="mt-10 hub-navy rounded-2xl p-8 relative overflow-hidden">
            <div className="relative">
              <h3 className="font-serif text-3xl text-white">¿Quieres a {pro.name?.split(" ")[0]} para tu barco?</h3>
              <p className="text-white/70 mt-2">Publica una solicitud en MarinaHub y le llegará automáticamente si está disponible en tu zona.</p>
              <Link to={user ? "/owner" : "/register?role=owner"}><button className="mt-5 hub-btn-coral hub-btn-primary gap-2 inline-flex items-center" data-testid="pp-cta">
                {user ? "Publicar solicitud" : "Crear cuenta gratis"} <ArrowRight size={14}/>
              </button></Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
